import React from 'react'
import useTranslation from 'next-translate/useTranslation'
import { twMerge } from 'tailwind-merge'

import { RestrictableContent } from '../utils/types'
import { CircleChevronIcon } from '../icons/circle-chevron'

export type DescriptionSummaryProps = {
  descriptionSummary: string[]
  isVisible?: boolean
  onToggle?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void
  columns?: number
  className?: string
  titleKey?: string | null
} & RestrictableContent

export const DescriptionSummary = ({
  descriptionSummary,
  isVisible,
  onToggle,
  isRestricted,
  columns = 2,
  className,
  titleKey = 'listings:quick-summary',
}: DescriptionSummaryProps) => {
  const { t } = useTranslation()

  const listColumns = descriptionSummary
    // Arranging the items to flow vertically into each column
    .reduce(
      (acc, point, indx) => {
        acc[indx % columns]?.push(point)
        return acc
      },
      Array.from({ length: columns }, () => [] as string[])
    )
    .map((group, indx, arr) => {
      if (!group.length) {
        return null
      }

      return (
        <React.Fragment key={group.join('')}>
          <ul
            className={twMerge(
              'flex-1 list-disc pl-[18px] leading-7 [&:last-child]:pr-2',
              columns <= 1 ? 'pt-3.5' : ''
            )}
          >
            {group.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          {indx !== arr.length - 1 ? (
            <span className="border-shades-200 ml-7 mr-5 mt-1.5 mb-1 h-auto w-px border-r border-dashed" />
          ) : null}
        </React.Fragment>
      )
    })

  const canToggle = !!onToggle

  return descriptionSummary.length && isRestricted === undefined ? (
    <details open={isVisible || !canToggle} className={twMerge('px-[18px] py-[19px]', className)}>
      <summary
        className="list-none [&::-webkit-details-marker]:hidden"
        // When JS is enabled, defer opening state to show more button only
        onClick={(e) => {
          if (canToggle) {
            onToggle?.(e)
          }
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <div className={`relative flex items-center font-bold ${canToggle ? 'cursor-pointer' : ''}`}>
          {titleKey ? <h2>{t(titleKey)}</h2> : null}
          {/* TODO: Potentially move away from a details with toggle if this is only for mobile and toggled another way? */}
          {canToggle ? (
            <CircleChevronIcon className={twMerge('absolute right-0', isVisible ? 'rotate-180' : '')} />
          ) : null}
        </div>
      </summary>
      {columns > 1 ? <ul className="flex justify-between pt-3.5">{listColumns}</ul> : listColumns}
    </details>
  ) : null
}
